import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

const CONSENT_KEY = 'nexuscms_consent';
const CONSENT_DAYS = 180;

/**
 * Read consent decision from localStorage. Returns 'accept' | 'reject' | null.
 * Expires after CONSENT_DAYS days.
 */
const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.decision || !data.ts) return null;
    if (Date.now() - data.ts > CONSENT_DAYS * 24 * 3600 * 1000) return null;
    return data.decision;
  } catch (e) {
    return null;
  }
};

const writeConsent = (decision) => {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ decision, ts: Date.now() }));
  } catch (e) {
    /* ignore */
  }
};

/**
 * Parse an HTML string and append every node to `target`. For <script> tags
 * we create fresh script elements so the browser actually executes them
 * (innerHTML alone does NOT run inserted scripts).
 *
 * Returns the list of inserted nodes so they can be cleaned up on unmount.
 */
const injectHTMLString = (htmlString, target, marker) => {
  if (!htmlString || !htmlString.trim() || !target) return [];
  const holder = document.createElement('div');
  holder.innerHTML = htmlString;
  const inserted = [];
  Array.prototype.slice.call(holder.childNodes).forEach((node) => {
    let toAppend = node;
    if (node.tagName === 'SCRIPT') {
      const newScript = document.createElement('script');
      for (let i = 0; i < node.attributes.length; i += 1) {
        const attr = node.attributes[i];
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.text = node.textContent || '';
      toAppend = newScript;
    }
    if (toAppend.setAttribute) {
      toAppend.setAttribute('data-nexuscms-custom', marker);
    }
    target.appendChild(toAppend);
    inserted.push(toAppend);
  });
  return inserted;
};

/**
 * TenantCustomCode
 *
 * Fetches tenant-specific Custom Head/Body HTML (chat widgets, analytics, etc.)
 * and the Cookie Consent Banner configuration, then injects them into the live
 * DOM on the public tenant site. Gated by GDPR consent stored in localStorage.
 *
 * Props:
 *   subdomain: optional subdomain string (e.g. "schoolsuite")
 *   domain: optional custom domain string (e.g. "schooledge.ai")
 *   At least one of `subdomain` or `domain` must be provided.
 */
const TenantCustomCode = ({ subdomain, domain }) => {
  const [config, setConfig] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const activatedRef = useRef(false);
  const insertedNodesRef = useRef([]);

  const activateScripts = (cfg) => {
    if (!cfg || activatedRef.current) return;
    const headHtml = (cfg.custom_head_html || '').trim();
    const bodyHtml = (cfg.custom_body_html || '').trim();
    if (!headHtml && !bodyHtml) return;
    const inserted = [
      ...injectHTMLString(headHtml, document.head, 'head'),
      ...injectHTMLString(bodyHtml, document.body, 'body'),
    ];
    insertedNodesRef.current = inserted;
    activatedRef.current = true;
  };

  // Fetch the custom-code/cookie-banner config once on mount.
  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (subdomain) params.subdomain = subdomain;
    if (domain) params.domain = domain;
    if (!params.subdomain && !params.domain) return undefined;

    api
      .get('/public/custom-code', { params })
      .then((res) => {
        if (cancelled) return;
        setConfig(res.data || {});
      })
      .catch((err) => {
        // Soft-fail – tenant just won't get custom scripts on this page.
        // eslint-disable-next-line no-console
        console.warn('[TenantCustomCode] Failed to load custom code:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [subdomain, domain]);

  // Decide whether to inject scripts immediately, show the banner, or do nothing.
  useEffect(() => {
    if (!config) return;
    const headHtml = (config.custom_head_html || '').trim();
    const bodyHtml = (config.custom_body_html || '').trim();
    const hasCustom = headHtml || bodyHtml;

    // If banner is disabled, scripts are always injected (tenant accepts risk).
    if (!config.cookie_banner_enabled) {
      if (hasCustom) activateScripts(config);
      setShowBanner(false);
      return;
    }

    // Banner enabled – respect consent.
    const decision = readConsent();
    if (decision === 'accept') {
      if (hasCustom) activateScripts(config);
      setShowBanner(false);
    } else if (decision === 'reject') {
      setShowBanner(false);
    } else {
      // No decision yet – show banner.
      setShowBanner(true);
    }
  }, [config]);

  // Cleanup injected nodes if the component unmounts (e.g., navigating away
  // to a non-tenant route inside the SPA).
  useEffect(() => {
    return () => {
      insertedNodesRef.current.forEach((node) => {
        try {
          if (node && node.parentNode) node.parentNode.removeChild(node);
        } catch (e) {
          /* ignore */
        }
      });
      insertedNodesRef.current = [];
    };
  }, []);

  const handleAccept = () => {
    writeConsent('accept');
    setShowBanner(false);
    activateScripts(config);
  };

  const handleReject = () => {
    writeConsent('reject');
    setShowBanner(false);
  };

  if (!config || !showBanner) return null;

  const title = config.cookie_banner_title || 'We use cookies';
  const message =
    config.cookie_banner_message ||
    'We use cookies to enhance your browsing experience and analyse our traffic. By clicking Accept, you consent to our use of cookies.';
  const acceptText = config.cookie_banner_accept_text || 'Accept all';
  const rejectText = config.cookie_banner_reject_text || 'Reject';
  const policyUrl = (config.cookie_banner_policy_url || '').trim();

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        left: '16px',
        right: '16px',
        bottom: '16px',
        zIndex: 2147483646,
        background: '#0f172a',
        color: '#fff',
        borderRadius: '12px',
        padding: '16px 20px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        fontFamily:
          "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '520px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <strong style={{ fontSize: '15px', fontWeight: 600 }}>{title}</strong>
        <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, opacity: 0.9 }}>
          {message}
          {policyUrl && (
            <>
              {' '}
              <a
                href={policyUrl}
                style={{ color: '#fff', textDecoration: 'underline' }}
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </>
          )}
        </p>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="cookie-consent-accept"
          onClick={handleAccept}
          style={{
            flex: 1,
            minWidth: '120px',
            padding: '9px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: 0,
            borderRadius: '8px',
            background: '#6366f1',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {acceptText}
        </button>
        <button
          type="button"
          data-testid="cookie-consent-reject"
          onClick={handleReject}
          style={{
            flex: 1,
            minWidth: '120px',
            padding: '9px 16px',
            fontSize: '13px',
            fontWeight: 500,
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {rejectText}
        </button>
      </div>
    </div>
  );
};

export default TenantCustomCode;
